import { Component } from '@felicity/vision';
import {
  NodeManager, NodeAssistantManager, CriterionManager, SystemLevel,
  SystemSelector, IndicatorSelector, CloneNodeDialog
} from '@felicity/multi-criteria-analysis';
import SlidingPanelLayout from '../ui/SlidingPanelLayout.mjs';
import AssistantSelector from '../ui/AssistantSelector.mjs';

/**
 * Generic Frameworks screen
 */
export default class GenericFrameworks extends Component {
  /**
   * The languages supported by this screen
   * @type {string[]}
   */
  languages = [];

  /**
   * Whether assistant management features should be enabled
   * @type {boolean}
   * @default false
   */
  assistantManagement = false;

  /**
   * Provides access to the sliding panel layout
   * @type {SlidingPanelLayout}
   * @readonly
   */
  get slidingPanelLayout() {
    return this.#slidingPanelLayout;
  }

  /** @type {SlidingPanelLayout} */
  #slidingPanelLayout;

  constructor() {
    super();

    this.#slidingPanelLayout = new SlidingPanelLayout();
  }

  _prepare() {
    super._prepare();

    this.appendChild(this.slidingPanelLayout);
    this.classList.add('screen');

    const genericFrameworksManager = new NodeManager();
    genericFrameworksManager.languages = this.languages;
    genericFrameworksManager.crudUri = '/api/MCA/Node';
    genericFrameworksManager.autoBind = true;
    // eslint-disable-next-line no-template-curly-in-string
    genericFrameworksManager.urlTemplateOf('export', '/api/MCA/Node/${nodeId}/Export');
    if (this.assistantManagement) {
      genericFrameworksManager.assistantManagement = true;
      genericFrameworksManager.nodeAssistantSelectorClass = AssistantSelector;
    }

    this.slidingPanelLayout.addPanel('Generic Frameworks', genericFrameworksManager);

    genericFrameworksManager.addEventListener('open', (e) => {
      this.slidingPanelLayout.clear();

      const node = e.detail.rowData,
        { assessmentSystem } = node;

      if (!assessmentSystem) {
        return;
      }

      assessmentSystem.levels
        .sort((a, b) => a.rank - b.rank)
        .map((level) => SystemLevel.create(level, this.languages))
        .forEach((level) => {
          const nodeManager = new NodeManager();
          nodeManager.languages = this.languages;
          nodeManager.crudUri = '/api/MCA/Node';

          const panel = this.slidingPanelLayout.addPanel(level.name, nodeManager);

          // TODO check if there is still an id column in the grid
          nodeManager.grid[`${level.rank < node.assessmentSystem.depth ? 'show' : 'hide'}Columns`]('id', 'field');

          const criterionManagers = {};
          level.criterionTypeList.forEach((ct) => {
            const criterionManager = new CriterionManager();
            criterionManager.languages = this.languages;
            criterionManager.crudUri = '/api/MCA/Criterion';
            criterionManager.type = ct;
            criterionManagers[ct] = criterionManager;
            criterionManager.autoBind = false;

            const contentPanel = panel.querySelector('.panel-content'),

              header = document.createElement('h3');
            header.textContent = ct;
            contentPanel.appendChild(header);
            contentPanel.appendChild(criterionManagers[ct]);
          });

          nodeManager.on('open', (e2) => {
            if (level.rank < node.assessmentSystem.depth) {
              const panelIdx = level.rank + 1;
              this.#setParentIdToNodeManagerInPanel(panelIdx, e2.detail.rowData.nodeId);
              this.#enableOpenAction(panelIdx, level.rank + 1 < node.assessmentSystem.depth);
              this.slidingPanelLayout.showPanel(panelIdx);
            }
          });

          nodeManager.grid.addEventListener('rowSelected', (e3) => {
            Object.values(criterionManagers).forEach((cm) => {
              cm.nodeId = e3.data.nodeId; // eslint-disable-line no-param-reassign
            });
          });
          nodeManager.grid.addEventListener('rowDeselected', (e4) => {
            Object.values(criterionManagers).forEach((cm) => {
              cm.nodeId = null; // eslint-disable-line no-param-reassign
            });
          });
        });

      this.#setParentIdToNodeManagerInPanel(node.level + 1, node.nodeId);
      this.slidingPanelLayout.showPanel(node.level + 1);
    });
  }

  #setParentIdToNodeManagerInPanel(panelIdx, parentId) {
    const panel = this.slidingPanelLayout.panels[panelIdx];
    if (!panel) {
      return;
    }
    const nodeManager = panel.querySelector('flc-node-manager');
    if (nodeManager) {
      nodeManager.parentId = parentId || null;
    }
  }

  #enableOpenAction(panelIdx, enable) {
    const panel = this.slidingPanelLayout.panels[panelIdx];
    if (!panel) {
      return;
    }
    const nodeManager = panel.querySelector('flc-node-manager');
    nodeManager?.grid[enable ? 'showColumns' : 'hideColumns']('nodeId', 'field');
  }
}

SlidingPanelLayout.register('sliding-panel-layout');
SystemSelector.register('system-selector');
AssistantSelector.register('assistant-selector');
NodeAssistantManager.register('node-assistant-manager');
NodeManager.register('node-manager');
CriterionManager.register('criterion-manager');
IndicatorSelector.register('indicator-selector');
CloneNodeDialog.register('clone-node-dialog');
